import { observable, action, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { validationContext, required, func, pattern } from 'validx'
import { generateSlugFromText } from 'utils/slug-util'
import TeamStore from 'iam/TeamStore'
import TeamMemberStore from 'iam/TeamMemberStore'
import { Id } from 'types/definitions'
import Workspace from 'iam/models/Workspace'

/**
 * New team state
 */
export default class NewTeamModalViewModel {
  @observable showing = false
  @observable name = ''
  @observable slug = ''

  validation = validationContext<NewTeamModalViewModel>(this, {
    name: [
      required('Team name is required'),
      func(({ value }) => {
        return value.length <= 50
      }, "Name can't be more than 50 characters"),
    ],
    slug: [
      required('Slug is required'),
      func(
        ({ value }) => value && value.length > 2,
        'Must be longer than 2 characters',
      ),
      pattern(
        /^[a-zA-Z]+[a-zA-Z0-9-]*[a-zA-Z0-9]$/,
        'Must be alphanumerical including dashes (except at the beginning and end)',
      ),
      (opts) =>
        pattern(/([-]{2,})/g)(opts) !== true
          ? true
          : 'Consecutive dashes are not allowed',
    ],
  })

  constructor(
    private teamMemberStore: TeamMemberStore,
    private teamStore: TeamStore,
    private workspace: Workspace,
  ) {
    makeObservable(this)
    this.createTeam = this.createTeam.bind(this)
  }

  @action.bound
  show() {
    this.name = ''
    this.slug = ''
    this.validation.reset()
    this.showing = true
  }

  @action.bound
  suggestSlug() {
    if (this.slug) {
      return
    }

    this.slug = generateSlugFromText(this.name)
  }

  @action.bound
  close() {
    this.showing = false
  }

  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  createTeam = task.resolved(async () => {
    if (!this.validate()) {
      return
    }
    const team = await this.teamStore.createTeam({
      name: this.name,
      slug: this.slug,
      workspace_id: Id.assert(this.workspace.id),
    })
    await this.teamMemberStore.fetchUserTeamMembersForWorkspace({
      user_id: Id.assert(team.owner.member.user.publicId),
      workspace_id: Id.assert(this.workspace.id),
    })
    this.close()
  })
}
